import { useRef, useEffect } from 'react';
import { useInView } from '@hooks/useScroll';
import { Waves } from 'lucide-react';
import { clubInfo } from '@/data/chkz';
import { useSiteText } from '@/lib/SiteTextContext';
import { useSiteImages } from '@/lib/SiteImagesContext';
import { useReducedMotion } from '@/hooks/useScroll';

export function WaterSection() {
  const { t } = useSiteText();
  const { img } = useSiteImages();
  const { ref, isInView } = useInView();
  const reduced = useReducedMotion();
  const glowRef = useRef<HTMLDivElement>(null);

  // Lightweight parallax: write the transform straight to the DOM node
  // inside rAF — no React state, so scrolling never re-renders the section.
  useEffect(() => {
    if (reduced) return;
    const el = glowRef.current;
    if (!el) return;
    let ticking = false;
    const update = () => {
      el.style.transform = `translate3d(0, ${window.scrollY * 0.08}px, 0)`;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      el.style.transform = '';
    };
  }, [reduced]);

  return (
    <section id="about" className="relative py-24 md:py-32 bg-foam-white overflow-hidden">
      <div
        ref={glowRef}
        className={`absolute inset-0 transition-all duration-1000 ease-sail ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
        style={{
          backgroundImage:
            'radial-gradient(circle at center, hsla(200, 90%, 85%, 0.3) 0%, transparent 70%)',
        }}
      />

      <div ref={ref} className="container mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div
            className={`reveal transition-all duration-700 ease-sail ${
              isInView ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'
            }`}
            style={{ transitionDelay: '120ms' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Waves className="w-6 h-6 text-ocean-blue" />
                          <span className="text-sm tracking-[0.3em] uppercase text-ocean-blue font-medium">
              {t('about.label')}
            </span>
          </div>

          <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight mb-6 text-ocean-blue">
            {t('about.title')}
          </h2>

          <p className="text-lg text-text-muted font-light leading-relaxed mb-6">
            {t('hero.tagline')}
          </p>

          <p className="text-lg text-text-muted font-light leading-relaxed mb-6">
            {t('about.text')}
          </p>

          <div className="flex flex-col gap-3 text-text-muted font-light">
            <p className="flex items-start gap-2">
              <span className="text-accent-gold">●</span> {clubInfo.email}
            </p>
          </div>
          </div>

          <div
            className={`relative h-80 lg:h-96 rounded-3xl overflow-hidden shadow-2xl transition-all duration-700 ease-sail ${
              isInView ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'
            } ${reduced ? '' : 'img-reveal img-reveal-visible'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <img
              src={img('aboutHero')}
              alt="ChKŻ LOK - trening na wodzie"
              loading="lazy"
              width={1280}
              height={960}
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-sail hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ocean-blue/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <p className="font-bold text-3xl">⛣️</p>
              <p className="text-sm opacity-90 mt-1">Jezioro Miejskie, Chodzież</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
